// frontend/src/components/ui/LegalDialog.tsx
// In-App-Pop-Up fuer Rechtstexte (Impressum/Datenschutz/AGB/Nutzungsbedingungen).
//
// F-09: Ersetzt die kaputte iframe-Anzeige (Helmet X-Frame-Options: DENY blockt
// das iframe selbst same-origin; nativ zusaetzlich cross-origin). Statt eines
// iframes holt das Pop-Up den lokalisierten Body als HTML-FRAGMENT vom Server
// (GET /api/legal/:doc) und rendert es inline.
//
// C-09: Beim Datenschutz-Fragment bleibt der Original-Zustand der
// Lesefassungen erhalten (simple sichtbar, full hidden) und die
// .legal-version-switch-Buttons werden mitgeliefert. Die Umschaltung uebernimmt
// useLegalVersionSwitch() per Event-Delegation (kein inline-Script → CSP-sicher).
// R9-05: Diese Logik lag frueher NUR hier und fehlte auf der Vollroute
// /legal/:doc — dort waren die Knoepfe tot. Sie liegt jetzt gemeinsam in
// ../../legal/legalFragment.ts und wird von beiden Stellen genutzt.
//
// Schliessen/Zurueck: Wir nutzen ResponsiveDialog → DialogBase, das via
// useModalBackClose den zentralen Back-Handler (window.LorathBackHandler →
// registerCloser/unregisterCloser aus native/bridge.ts) bedient. Dadurch
// schliessen Hardware-Back (Android), iOS-Edge-Swipe und Escape das Pop-Up und
// fuehren GENAU dorthin zurueck, wo der User war (Onboarding-Schritt / Settings).

import { useEffect, useState } from 'react';
import { getLegalFragment } from '../../api/legal';
import { useT } from '../../i18n';
import { sanitizeLegalHtml, useLegalVersionSwitch } from '../../legal/legalFragment';
import type { LegalServerSlug } from '../../legal/legalSlugs';
import { ResponsiveDialog } from './ResponsiveDialog';
import { Skeleton } from './Skeleton';
import styles from './LegalDialog.module.css';

export type LegalDocId = LegalServerSlug;

export interface LegalDialogProps {
  /** Whitelisted doc-Key — null/undefined laesst das Pop-Up geschlossen. */
  doc: LegalDocId | null;
  onClose: () => void;
  /** Rendert den Dialog ueber einem blockierenden App-Gate. */
  layer?: 'default' | 'foreground';
}

// Titel-Key pro Dokument (bestehende i18n-Keys, KEINE neuen Keys hinzufuegen).
const DOC_TITLE_KEYS: Record<LegalDocId, string> = {
  impressum: 'legal.impressum.title',
  datenschutz: 'legal.privacy.title',
  agb: 'legal.agb.title',
  nutzungsbedingungen: 'legal.use_terms.title',
};

export function LegalDialog({ doc, onClose, layer = 'default' }: LegalDialogProps) {
  const { t, lang } = useT();
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [html, setHtml] = useState<string>('');
  // Content-Container als STATE (Callback-Ref), nicht als useRef: Daran haengt
  // die Klick-Delegation des Versionsumschalters. ResponsiveDialog rendert je
  // nach Breite <Sheet> oder <Modal>; kippt der Viewport bei offenem Dialog
  // ueber die 600-px-Schwelle, wird der Content-<div> neu gemountet. Nur ein
  // State-Wert aendert sich dabei und laesst den Hook neu anhaengen — ein
  // Ref-Objekt bliebe identisch und der Listener am alten Knoten haengen
  // (R9-05-Review FE-01).
  const [contentRoot, setContentRoot] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!doc) {
      setStatus('idle');
      setHtml('');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    setHtml('');

    // Gemeinsamer Helfer fuer App-Sprache und Cache-Revalidierung wie auf
    // der Vollroute; neue Uebersetzungen werden auch nach einer OTA sichtbar.
    getLegalFragment(doc, lang)
      .then((fragment) => {
        if (cancelled) return;
        setHtml(typeof fragment === 'string' ? fragment : '');
        setStatus('loaded');
      })
      .catch(() => {
        // Egal ob ApiError (z.B. 404) oder Netzwerk/Offline — gleiche Fehlermeldung.
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // R9-05: `lang` gehoert in die Abhaengigkeiten — siehe pages/legal/index.tsx.
  }, [doc, lang]);

  // C-09/R9-05: Klick-Delegation fuer den Datenschutz-Versionsumschalter
  // ("Einfache Sprache" / "Vollstaendig"). Gemeinsame Logik mit der Vollroute.
  // contentRoot ist nur gesetzt, solange der Container gerendert ist
  // (status === 'loaded') — sonst null → No-Op.
  useLegalVersionSwitch(contentRoot, html);

  if (!doc) return null;

  const title = t(DOC_TITLE_KEYS[doc]);

  return (
    <ResponsiveDialog open onClose={onClose} title={title} maxWidth={720} layer={layer}>
      {/* KI-Uebersetzungs-Disclaimer wie auf der Vollseite nur fuer Nicht-DE
          (DE = rechtsverbindliches Original). First-Party-Text aus i18n —
          kommt aber aus den maschinell erzeugten Uebersetzungen aller
          Fremdsprachen und durchlaeuft serverseitig KEINEN Sanitizer
          (sanitizeI18nHtml wirkt nur in transformHtml fuer statische Seiten).
          R9-05-Review (SEC-03): deshalb dieselbe zweite Verteidigungslinie
          wie fuer das Fragment darunter. */}
      {lang !== 'de' ? (
        <p
          className={`${styles.disclaimer} prose-hyphens`}
          dangerouslySetInnerHTML={{ __html: sanitizeLegalHtml(t('legal.ai_disclaimer_html')) }}
        />
      ) : null}

      {status === 'loading' ? (
        <div className={styles.skeletonStack} aria-hidden="true">
          <Skeleton variant="line" size="lg" length="medium" />
          <Skeleton variant="line" length="long" />
          <Skeleton variant="line" length="long" />
          <Skeleton variant="line" length="medium" />
          <Skeleton variant="line" length="short" />
          <Skeleton variant="card" card="tall" />
        </div>
      ) : null}

      {status === 'error' ? (
        <p className={styles.error} role="alert">
          {t('legal.embed_load_error')}
        </p>
      ) : null}

      {status === 'loaded' ? (
        // BEWUSST dangerouslySetInnerHTML: Der Inhalt ist vertrauenswuerdiger
        // First-Party-Inhalt — serverseitig aus unseren eigenen statischen
        // public/<doc>.html erzeugt (kein User-Input, Server entfernt alle
        // <script>-Tags). Als zweite Verteidigungslinie laeuft das Fragment
        // durch sanitizeLegalHtml() (Regex-basierte Strips fuer <script>,
        // on*-Handler und javascript:-URLs), bevor es hier gerendert wird.
        <div
          ref={setContentRoot}
          className={`${styles.content} prose-hyphens`}
          dangerouslySetInnerHTML={{ __html: sanitizeLegalHtml(html) }}
        />
      ) : null}
    </ResponsiveDialog>
  );
}
